import { getProjectId } from './PlanningApiClient';

/**
 * A project-partitioned in-memory cache.
 *
 * The dashboard's per-domain services (budget, tasks, guests, events) used to
 * hold ONE module-level singleton array for the whole browser session. Because
 * the app is a multi-project (B2B) portfolio — one planner switches between many
 * projects, and the workspace Overview reads the same services — that singleton
 * showed whichever project's data happened to populate it last. Switching
 * projects (or the Overview reading a project service) leaked another project's
 * numbers wholesale (the KPI "flap" / cross-project bleed data-integrity bug).
 *
 * This keys the cache off the ACTIVE project id — the same id
 * PlanningApiClient.getProjectId() / localStorage[kalligo_project_id] returns,
 * and the same id the GraphQL client sends as x-project-id on every request —
 * so load()/save() only ever touch the active project's entry. A brand-new
 * project (no entry yet) reads empty, never a sibling's leftover cache.
 *
 * `makeEmpty` returns a FRESH empty value each call so callers that mutate the
 * returned array in place (e.g. GuestService.createGuest) never share a default.
 */
export class ProjectScopedCache<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly makeEmpty: () => T) {}

  private key(): string {
    return getProjectId();
  }

  /** The active project's cached value, or a fresh empty value if none cached yet. */
  load(): T {
    const key = this.key();
    return this.store.has(key) ? (this.store.get(key) as T) : this.makeEmpty();
  }

  /** Overwrite the active project's cached value (including with an empty one). */
  save(value: T): void {
    this.store.set(this.key(), value);
  }

  /** Whether the active project has a cached value (distinguishes "never fetched" from "fetched empty"). */
  has(): boolean {
    return this.store.has(this.key());
  }

  /** The active project's key right now. Take it BEFORE an await, so the result lands where the request was made. */
  currentKey(): string {
    return this.key();
  }

  /** The value cached for a given project (a fresh empty value when none). */
  loadFor(key: string): T {
    return this.store.has(key) ? (this.store.get(key) as T) : this.makeEmpty();
  }

  /** Save for a given project, whichever project is active now (a request that finishes after a project switch). */
  saveFor(key: string, value: T): void {
    this.store.set(key, value);
  }

  /** Test/reset hook — drop every project's entry. */
  clear(): void {
    this.store.clear();
  }

  /** Drop only the ACTIVE project's entry, forcing the next load()/has() to
   *  treat it as never-fetched — for callers that want to force a full
   *  refetch after a write rather than locally patching the cached value. */
  invalidate(): void {
    this.store.delete(this.key());
  }
}
